import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from "@tanstack/react-query"
import { AxiosResponse } from "axios"

import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import { queryKeys } from "api/queryKeys"
import {
  documentUpload,
  documentDelete,
  documentDownload,
  documentFolderCreate,
  documentFolderDelete,
} from "api/Repos/documents"

/**
 * React-query mutations for the Documents domain (queries live in
 * `useDocumentsQuery.ts`).
 *
 * No baked-in notifications: the document call sites own context-specific
 * messages (file names, antd Upload status flow). Mutations invalidate the
 * project's documents so every list view refreshes automatically.
 */

interface UploadDocumentArgs {
  projectId: string
  owner: string
  filename: string
  file: File | Blob
  folder?: string
  overwrite?: boolean
}

interface DeleteDocumentItemArgs {
  projectId: string
  owner: string
  /** Full path of the file or folder to delete. */
  path: string
  isFolder?: boolean
}

interface CreateDocumentFolderArgs {
  projectId: string
  owner: string
  folderPath: string
}

interface DownloadDocumentArgs {
  projectId: string
  owner: string
  /** Full path of the file to download. */
  path: string
}

export const useUploadDocumentMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  UploadDocumentArgs
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      owner,
      filename,
      file,
      folder = "",
      overwrite = true,
    }: UploadDocumentArgs) =>
      documentUpload(
        await getAxios(),
        projectId,
        owner,
        filename,
        file,
        folder,
        overwrite
      ),
    onSuccess: (_res, { projectId }) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.project(projectId),
      }),
  })
}

/** Deletes a document or a folder (picks the endpoint by `isFolder`). */
export const useDeleteDocumentItemMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  DeleteDocumentItemArgs
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      owner,
      path,
      isFolder = false,
    }: DeleteDocumentItemArgs) => {
      const axios = await getAxios()
      return isFolder
        ? documentFolderDelete(axios, projectId, owner, path)
        : documentDelete(axios, projectId, owner, path, isFolder)
    },
    onSuccess: (_res, { projectId }) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.project(projectId),
      }),
  })
}

export const useCreateDocumentFolderMutation = (): UseMutationResult<
  AxiosResponse<unknown>,
  Error,
  CreateDocumentFolderArgs
> => {
  const getAxios = useAuthedAxios()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ projectId, owner, folderPath }) =>
      documentFolderCreate(await getAxios(), projectId, owner, folderPath),
    onSuccess: (_res, { projectId }) =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.project(projectId),
      }),
  })
}

/** Triggers a browser download of a document; no cache impact. */
export const useDownloadDocumentMutation = (): UseMutationResult<
  void,
  Error,
  DownloadDocumentArgs
> => {
  const getAxios = useAuthedAxios()

  return useMutation({
    mutationFn: async ({ projectId, owner, path }) =>
      documentDownload(await getAxios(), projectId, owner, path),
  })
}
